import type { KnowledgeArticle } from '../types/knowledge-base.type';
import { getKnowledgeArticleTranslations } from '../locales/help/articles';
import marketI18n from '../i18n';
import type { HelpLanguage } from '../i18n/help';
import { getHelpTextDirection, isHelpRtl } from '../i18n/help';
import { localizeHelpUiTerms } from './help-ui-terms.helper';

function localizeArticleField(text: string, language: HelpLanguage): string {
  if (language === 'en') return text;
  return localizeHelpUiTerms(text, language, marketI18n.t.bind(marketI18n));
}

export function localizeKnowledgeArticle(article: KnowledgeArticle): KnowledgeArticle {
  const language = marketI18n.language.split('-')[0] as HelpLanguage;
  const translation = getKnowledgeArticleTranslations(language)?.[article.id];
  if (!translation) return article;

  return {
    ...article,
    title: localizeArticleField(translation.title, language),
    summary: translation.summary
      ? localizeArticleField(translation.summary, language)
      : article.summary,
    content: localizeArticleField(translation.content, language),
  };
}

export function localizeKnowledgeArticles(articles: KnowledgeArticle[]): KnowledgeArticle[] {
  return articles.map(localizeKnowledgeArticle);
}

export function knowledgeArticleMatchesSearch(
  article: KnowledgeArticle,
  query: string
): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  const localized = localizeKnowledgeArticle(article);
  const translations = getKnowledgeArticleTranslations(marketI18n.language);

  const searchableText = [
    article.title,
    article.summary,
    article.content,
    localized.title,
    localized.summary,
    localized.content,
    translations?.[article.id]?.title,
    translations?.[article.id]?.content,
    ...article.tags,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return searchableText.includes(normalizedQuery);
}

export { getHelpTextDirection, isHelpRtl };
